import type { Applog } from '@wovin/core/applog'
import type { Thread } from '@wovin/core/thread'
import type { TiptapContent } from './TypeMap'

import type { VMstatic } from './utils-typemap'
import { getRegexForTagInContext } from '@note3/utils'

import { lastWriteWins, liveEntityAt, liveFilterAndMap, queryDivergencesByPrev, rollingFilter, withoutDeleted } from '@wovin/core'

import { useEternalSubscribable } from '@wovin/hooks-solid'
import { Logger } from 'besonders-logger'
import { debounce } from 'lodash-es'
import { createRoot, mapArray } from 'solid-js'
import { getEditorForBlockID, persistBlockContent } from '../block-utils'
import {
	compareBlockContent,
	replaceInTiptap,
} from '../block-utils-nowin'
import { BLOCK_DEF, REL_DEF } from '../data-types'

import {
	getAllTagsInText,
	parseBlockContentValue,
	tiptapToPlaintext,
} from '../note3-utils-nodeps'

import { orderBlockRelations } from '../relation-utils'
import { parseSmartQuery } from '../smart-list'
import { ObjectBuilder } from './builder'
import { getMappedVMtoExtend, getUseFx } from './MappedVMbase'
import { useRel } from './RelationVM'
import { knownAtMap } from './utils-typemap'

const { WARN, LOG, DEBUG, VERBOSE, ERROR } = Logger.setup(Logger.INFO)

export const { Block: BLK } = knownAtMap
const debounceWait = 1500

// Global registry of BlockVMs with pending content updates
const pendingFlushes = new Set<BlockVM>()

export type Block = VMstatic<'Block'>
export interface BlockVM extends Block {} // eslint-disable-line ts/no-unsafe-declaration-merging
// export class BlkBuilder extends ObjectBuilder<blkT> {}

const BLOCK = 'Block' as const
export class BlockVM extends getMappedVMtoExtend(BLOCK, undefined, ['content']) {
	public editingPos: number = null
	private editingBasedOn: Applog = null // ? Applog ? or rather pv/cid ?

	_content: TiptapContent | null

	get editor() {
		return getEditorForBlockID(this.en)
	}

	get selection() {
		return this.editor?.view?.state?.selection
	}

	get selectionText() {
		return this.selection?.content?.content
	}

	get exists() {
		return !this.entityThread.isEmpty // HACK: use a query that's less expensive
	}

	// kidRelations, kidVMs, parentRelations are set up as lazy cached properties in initialize()
	declare kidRelations: ReturnType<typeof orderBlockRelations>
	declare kidVMs: BlockVM[]
	declare parentRelations: any[]

	get currentRelation() { // HACK: BlockVM doesn't know about view context
		const rels = this.parentRelations
		return rels?.find(eachRel => eachRel.block === this.en)
	}

	persistContent = async (newContent: TiptapContent) => {
		return persistBlockContent(this.thread, this.en, newContent, this.editingBasedOn?.cid)
	}

	markNotEditing = () => {
		this.editingBasedOn = this.editingPos = null
		pendingFlushes.delete(this)
	}

	debouncedSetContent = debounce(this.persistContent.bind(this), debounceWait, { leading: false /* 'maxWait': 5000  */ }) // HACK re-enable maxWait (when block/content insert performance is not an issue anymore)
	debouncedMarkNoEdit = debounce(this.markNotEditing.bind(this), debounceWait + 100)

	get isEditing() {
		return !!this.editingBasedOn
	}

	get isEmpty() {
		return !this.contentPlaintext?.trim() && this.isText
	}

	get historicContentLogs() {
		return this.entityThread?.applogs?.filter(l => l.at === BLOCK_DEF.content)
	}

	get contentApplog() {
		return this.entityThread?.applogs?.findLast(l => l.at === BLOCK_DEF.content) // HACK: use divergence handling
	}

	get contentFromApplog() {
		return this.contentApplog?.vl as string
	}

	get content() {
		if (!this._content) {
			VERBOSE(this.en, 'Empty _content', this)
		}
		return this._content
	}

	set content(newContent: TiptapContent) {
		if (this.thread.readOnly) throw ERROR(`[BlockVM.content] setter called but readOnly thread`, { en: this.en, newContent })
		if (this._content === newContent) {
			VERBOSE('BlockVM noop setting content', newContent)
		} else {
			this._content = newContent // optimistic ui, BlockVM debounces persist
			pendingFlushes.add(this)
			this.debouncedSetContent(newContent) // lazy atom writing - if you need to avoid optimistic ui and rely on observables use setContent directly
			this.editingBasedOn = this.contentApplog
			this.debouncedMarkNoEdit()
		}
	}

	cancelDebouncedContent() {
		this.debouncedSetContent.cancel()
		this.debouncedMarkNoEdit.cancel()
		pendingFlushes.delete(this)
	}

	get contentDivergences() {
		const divergences = queryDivergencesByPrev(
			rollingFilter(this.entityThread, { at: BLOCK_DEF.content }),
		)
		if (divergences.length <= 1) {
			return null
		}
		DEBUG('Found content divergence: ', divergences)
		return divergences satisfies readonly { log: Applog, thread: Thread }[]
	}

	get smartQuery() {
		const text = this.contentPlaintext // ? smartQuery separate from content
		if (!text) return null
		return parseSmartQuery(text)
	}

	get contentPlaintext() {
		DEBUG(`[contentPlaintext] input:`, this.content)
		const text = tiptapToPlaintext(this.content)
		DEBUG(`[contentPlaintext] output:`, text)
		return text
	}

	getTagsIncludingSubtags(tag: string) {
		if (!this.contentPlaintext) return []
		return getAllTagsInText(this.contentPlaintext, getRegexForTagInContext(tag, 'g'))
	}

	get tags() {
		return getAllTagsInText(this.contentPlaintext)
	}

	get isText() {
		return !this.type || this.type === 'text'
	}

	get isReply() {
		return this.currentRelation?.isReply
	}

	get isTodo() {
		if (!this.isText) return false
		return this.getTagsIncludingSubtags('+todo').length > 0
	}

	get isTodoDone() {
		return this.getTagsIncludingSubtags('+todo/done').length > 0
	}

	setTodoDone = (newVal = true) => {
		const todoTags = Array.from(this.contentPlaintext.matchAll(getRegexForTagInContext(newVal ? '+todo' : '+todo/done', 'g')))
		DEBUG(`[setTodoDone] matches`, todoTags)
		if (todoTags.length === 0) throw ERROR(`[setTodoDone] no todo tags`, { newVal, content: this.contentPlaintext })
		if (todoTags.length > 1) WARN(`[setTodoDone] multiple todo tags`, { newVal, content: this.contentPlaintext, todoTags })
		if (newVal && todoTags[0][0].includes('/done')) {
			WARN(`[setTodoDone] tag is already done`, { newVal, content: this.contentPlaintext, todoTags })
		}
		const newContent = replaceInTiptap(
			this.content,
			newVal ? /\+todo(\/doing)?/ : /\+todo\/done/,
			newVal ? '+todo/done' : '+todo',
		)
		this.content = newContent
	}

	toggleSurvey = () => {
		const type: typeof this.type = this.type === 'survey' ? 'text' : 'survey'
		const update = { type }
		this.buildUpdate(update).commit(this.thread as any)
		return type
	}

	toggleSmartlist = () => {
		const type: typeof this.type = this.type === 'smartlist' ? 'text' : 'smartlist'
		const update = { type }
		this.buildUpdate(update).commit(this.thread as any)
		return type
	}

	initialize = (instanceRef: InstanceType<typeof BlockVM>, thread: Thread) => {
		VERBOSE(`Init BlockVM`, instanceRef)
		const blockID = instanceRef.en
		if (!blockID) {
			throw ERROR(`Empty BlockID`, instanceRef)
		}

		// Subscribe to content changes via liveEntityAt (replaces MobX Reaction)
		let currentThread = lastWriteWins(thread, { tolerateAlreadyFiltered: true })
		if (!currentThread.filters.includes('withoutDeleted')) currentThread = withoutDeleted(currentThread) as any
		const contentSubscribable = liveEntityAt(currentThread, blockID, BLOCK_DEF.content)

		// Set initial content
		const initialContent = contentSubscribable.value as string ?? null
		if (initialContent !== null && typeof initialContent !== 'string') {
			WARN(`[BlockVM] block/content is not null|string:`, initialContent, { blockID })
		}
		instanceRef._content = parseBlockContentValue(initialContent)
		VERBOSE(`[BlockVM#${blockID}] block/content init:`, instanceRef._content)

		// Subscribe to future changes
		contentSubscribable.subscribe(() => {
			let newContent = contentSubscribable.value as string ?? null
			if (newContent === undefined) newContent = null
			if (newContent !== null && typeof newContent !== 'string') {
				WARN(`[BlockVM] block/content is not null|string:`, newContent, { blockID })
			}
			const parsedNew = parseBlockContentValue(newContent)
			VERBOSE(`[BlockVM#${blockID}] block/content update:`, parsedNew, {
				editing: instanceRef.editingBasedOn,
				old: instanceRef.content,
			})
			if (!instanceRef.editingBasedOn) {
				if (compareBlockContent(instanceRef._content, parsedNew)) {
					VERBOSE('skipping content update via subscription', {
						newContent,
						oldContent: instanceRef.content,
						isEditing: instanceRef.editingBasedOn,
					})
				} else {
					instanceRef._content = parsedNew
				}
			} else if (!compareBlockContent(instanceRef._content, parsedNew)) {
				VERBOSE(`content update while isEditing`, { newContent, current: instanceRef.content, isEditing: instanceRef.editingBasedOn })
			}
		})

		// Set up lazy cached list properties for kid relations, kid VMs, and parent relations
		const kidRelIDsSource = liveFilterAndMap(currentThread, { at: REL_DEF.childOf, vl: instanceRef.en }, 'en')
		const kidRelIDs = useEternalSubscribable(kidRelIDsSource)

		const parentRelIDsSource = liveFilterAndMap(currentThread, { vl: instanceRef.en, at: REL_DEF.block }, 'en')
		const parentRelIDs = useEternalSubscribable(parentRelIDsSource)

		const kidRelVMs = createRoot(() => mapArray(kidRelIDs, relID => useRel(relID, thread)))
		const parentRelVMs = createRoot(() => mapArray(parentRelIDs, relID => useRel(relID, thread)))

		Object.defineProperty(instanceRef, 'kidRelations', {
			get() {
				return orderBlockRelations(kidRelVMs())
			},
			enumerable: true,
			configurable: true,
		})

		Object.defineProperty(instanceRef, 'kidVMs', {
			get() {
				const kidRels = instanceRef.kidRelations
				return kidRels.map(rel => BlockVM.get(rel.block as string, thread))
			},
			enumerable: true,
			configurable: true,
		})

		Object.defineProperty(instanceRef, 'parentRelations', {
			get() {
				return parentRelVMs()
			},
			enumerable: true,
			configurable: true,
		})
	}
}

/**
 * Flushes all pending block content updates by immediately executing their debounced functions.
 * Used before sync to ensure all local edits are persisted before syncing with remote.
 * Also called on page unload to prevent data loss.
 *
 * @returns Promise that resolves when all pending flushes complete
 */
export async function flushAllPendingBlockContent(): Promise<void> {
	if (pendingFlushes.size === 0) {
		VERBOSE('[flushAllPendingBlockContent] no pending flushes')
		return
	}

	DEBUG(`[flushAllPendingBlockContent] flushing ${pendingFlushes.size} block(s)`)

	const flushPromises = Array.from(pendingFlushes).map((blockVM) => {
		return Promise.resolve().then(() => {
			try {
				// flush() triggers immediate execution of the debounced function
				blockVM.debouncedSetContent.flush()
				DEBUG(`[flushAllPendingBlockContent] flushed block ${blockVM.en}`)
			} catch (error) {
				ERROR(`[flushAllPendingBlockContent] error flushing block ${blockVM.en}:`, error)
				return Promise.reject(error)
			}
		})
	})

	const results = await Promise.allSettled(flushPromises)

	const errors = results
		.filter(result => result.status === 'rejected')
		.map(result => (result as PromiseRejectedResult).reason)

	if (errors.length > 0) {
		ERROR(`[flushAllPendingBlockContent] ${errors.length} error(s) during flush`, errors)
	} else {
		LOG(`[flushAllPendingBlockContent] successfully flushed all pending blocks`)
	}
}

export const BlockBuilder = ObjectBuilder<VMstatic<typeof BLOCK>> // TODO: runtime/typebox checking is not actually bound here, just TS generics
export const useBlk = getUseFx<typeof BlockVM, typeof BlockBuilder, typeof BLOCK>(BLOCK, BlockVM, BlockBuilder)
if (typeof window !== 'undefined') (window as any).useBlk = useBlk
